import { Collapse } from "../common/collapse";

/**
 * Dropdown + mobile-collapse behaviour for the navigation.
 *
 * - A dropdown is a `.w-dropdown` element; its open state is the `.w-open` class.
 * - Opening a dropdown closes any other open dropdown; a click elsewhere or the
 *   Escape key closes all of them.
 * - The mobile menu (`.w-navbar-collapse`) is shown/hidden with the Collapse
 *   height-animation helper and stays visible from 768px up.
 */
export class NavigationHelper
{
    static readonly openClass = "w-open";
    static readonly dropdownClass = "w-dropdown";

    private static globalHandlersBound = false;
    private static collapseElement: HTMLElement | null = null;
    private static collapseButton: HTMLElement | null = null;
    private static desktopQuery: MediaQueryList | null = null;

    /** Wire a dropdown toggle so clicking it opens/closes its menu. */
    static registerDropdown(dropdown: HTMLElement, toggle: HTMLElement): void
    {
        this.ensureGlobalHandlers();
        toggle.addEventListener("click", (event) =>
        {
            event.preventDefault();
            // Stop the document handler from immediately closing what we open.
            event.stopPropagation();
            const wasOpen = dropdown.classList.contains(this.openClass);
            this.closeAllDropdowns();
            if (!wasOpen)
            {
                dropdown.classList.add(this.openClass);
                toggle.setAttribute("aria-expanded", "true");
            }
        });
    }

    /** Wire the mobile burger button to expand/collapse the menu. */
    static registerCollapseToggle(button: HTMLElement, collapse: HTMLElement): void
    {
        this.collapseElement = collapse;
        this.collapseButton = button;
        // Matches the 768px breakpoint used in the stylesheet.
        this.desktopQuery = window.matchMedia("(min-width: 768px)");

        const applyForViewport = () =>
        {
            // Desktop: always expanded with no inline height. Mobile: collapsed
            // until the burger is tapped.
            const desktop = this.desktopQuery!.matches;
            Collapse.init(collapse, desktop);
            button.setAttribute("aria-expanded", String(desktop));
        };
        applyForViewport();

        if (this.desktopQuery.addEventListener)
        {
            this.desktopQuery.addEventListener("change", applyForViewport);
        }
        else
        {
            // Older Safari
            this.desktopQuery.addListener(applyForViewport);
        }

        button.addEventListener("click", (event) =>
        {
            event.preventDefault();
            event.stopPropagation();
            if (!this.isDesktop())
            {
                const willOpen = button.getAttribute("aria-expanded") !== "true";
                Collapse.toggle(collapse);
                button.setAttribute("aria-expanded", String(willOpen));
            }
        });
    }

    private static closeAllDropdowns(): void
    {
        document.querySelectorAll(`.${this.dropdownClass}.${this.openClass}`).forEach((dropdown) =>
        {
            dropdown.classList.remove(this.openClass);
            dropdown.querySelector(".w-dropdown-toggle")?.setAttribute("aria-expanded", "false");
        });
    }

    /**
     * Dismiss the menu after a navigation: close any open dropdown and, on
     * mobile, collapse the menu (a no-op on desktop).
     *
     * Used by nav links. The global outside-click / Escape handlers
     * intentionally call only `closeAllDropdowns` instead: clicking outside
     * closes the dropdowns but leaves the mobile menu open.
     */
    static dismiss(): void
    {
        this.closeAllDropdowns();
        if (this.collapseElement && !this.isDesktop())
        {
            Collapse.hide(this.collapseElement);
            this.collapseButton?.setAttribute("aria-expanded", "false");
        }
    }

    private static isDesktop(): boolean
    {
        return !!this.desktopQuery && this.desktopQuery.matches;
    }

    private static ensureGlobalHandlers(): void
    {
        if (this.globalHandlersBound)
        {
            return;
        }
        this.globalHandlersBound = true;

        // A click anywhere outside an open toggle closes the dropdowns.
        document.addEventListener("click", () => this.closeAllDropdowns());

        document.addEventListener("keydown", (event) =>
        {
            if (event.key === "Escape")
            {
                this.closeAllDropdowns();
            }
        });
    }
}
